import { useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getVendorDetails, getVendorBankAccounts } from '../../../redux/actions/SocietyPortal/VendorAction/VendorAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import { resolveContractStatus } from '../../../utils/helpers/contractStatus';

const ViewVendor = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();

    const { vendorDetailsData, vendorDetailsLoading, vendorBankAccountsData, vendorBankAccountsLoading } = useSelector(state => state.VendorReducers);

    useEffect(() => {
        if (id) {
            dispatch(getVendorDetails({ vendorId: id }));
            dispatch(getVendorBankAccounts({ vendorId: id, societyId: selectedSocietyStorer.getSocietyId() }));
        }
    }, [dispatch, id]);

    // Map API response fields to UI-expected fields
    const vendor = useMemo(() => {
        if (!vendorDetailsData) return null;
        const d = vendorDetailsData;
        // Contact person still falls back to the linked login account: a vendor onboarded
        // against an existing user may have no contact_person of its own, and a name is
        // harmless. Phone and email do not — see below.
        const contactPerson = d.contact_person
            || (d.user ? `${d.user.first_name || ''} ${d.user.last_name || ''}`.trim() : '');
        return {
            id: d.vendor_id,
            name: d.business_name,
            status: d.is_active === 1 ? 'Active' : 'Inactive',
            contactPerson,
            category: d.category?.label || d.category?.name || '',
            // Deliberately not falling back to d.user — see Vendors.jsx.
            phone: d.phone || '',
            email: d.email || '',
            website: d.website || '',
            address: d.address || '',
            pincode: d.pincode || '',
            rating: d.rating || 0,
            servicesCount: d.services_count || 0,
            contractStatus: resolveContractStatus(d),
            contractStart: d.contract_start_date || '',
            contractEnd: d.contract_end_date || '',
            contractAmount: d.contract_amount || '',
            paymentTerms: d.payment_terms || '',
            vendorGlCode: d.vendor_gl_code || '',
            experienceYears: d.experience_years ?? '',
            notes: d.notes || '',
            gstNumber: d.gst_number || '',
            panNumber: d.pan_number || '',
            tanNumber: d.tan_number || '',
            msmeStatus: d.msme_status || '',
            rcmApplicable: d.rcm_applicable || 'N',
            tdsApplicable: d.tds_applicable || 'N',
            createdAt: d.created_at || '',
        };
    }, [vendorDetailsData]);

    const fmtDate = (v) => {
        if (!v) return '';
        const dt = new Date(v);
        if (Number.isNaN(dt.getTime())) return v;
        return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    };
    const fmtCurrency = (v) => {
        const n = Number(v);
        if (!v || Number.isNaN(n)) return '';
        return `₹ ${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const getCategoryIcon = (category) => {
        const icons = {
            'Plumber': 'mdi:pipe-wrench',
            'Electrician': 'mdi:flash',
            'Carpenter': 'mdi:hammer-screwdriver',
            'Painter': 'mdi:format-paint',
            'Cleaner': 'mdi:spray-bottle',
            'Security': 'mdi:shield-check',
            'Gardener': 'mdi:flower',
            'Pest Control': 'mdi:bug',
            'AC Repair': 'mdi:air-conditioner',
            'Water Purifier': 'mdi:water',
            'Catering': 'mdi:silverware-fork-knife',
            'Lift Maintenance': 'mdi:elevator-passenger',
            'Fire Safety': 'mdi:fire-extinguisher',
            'Housekeeping': 'mdi:broom',
            'Other': 'mdi:dots-horizontal-circle',
        };
        return icons[category] || 'mdi:store';
    };

    const getContractBadge = (status) => {
        const styles = {
            'Active': 'tw-bg-emerald-100 tw-text-emerald-700',
            'Pending': 'tw-bg-amber-100 tw-text-amber-700',
            'Expired': 'tw-bg-red-100 tw-text-red-700',
        };
        return styles[status] || 'tw-bg-gray-100 tw-text-gray-600';
    };

    // eslint-disable-next-line no-unused-vars
    const _renderStars = (rating) => {
        const fullStars = Math.floor(rating);
        const hasHalf = rating % 1 >= 0.5;
        return (
            <div className="tw-flex tw-items-center tw-gap-0.5">
                {[...Array(5)].map((_, i) => (
                    <Icon
                        key={i}
                        icon={i < fullStars ? 'mdi:star' : (i === fullStars && hasHalf ? 'mdi:star-half-full' : 'mdi:star-outline')}
                        className={`tw-text-lg ${i < fullStars || (i === fullStars && hasHalf) ? 'tw-text-amber-400' : 'tw-text-gray-300'}`}
                    />
                ))}
                <span className="tw-text-sm tw-text-gray-600 tw-ml-2">{rating}/5</span>
            </div>
        );
    };

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                <div className="tw-flex tw-items-center tw-gap-3">
                    <button
                        onClick={() => navigate('/society-portal/vendors')}
                        className="tw-p-2 tw-rounded-lg tw-bg-gray-100 hover:tw-bg-gray-200 tw-text-gray-600 tw-flex tw-items-center tw-justify-center"
                    >
                        <Icon icon="mdi:arrow-left" className="tw-text-lg" />
                    </button>
                    <div>
                        <h1 className="tw-text-lg tw-font-semibold tw-text-gray-800">Vendor Details</h1>
                        <p className="tw-text-xs tw-text-gray-500">View vendor information</p>
                    </div>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    <button
                        onClick={() => navigate(`/society-portal/vendors/edit/${id}`)}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-bg-blue-600 tw-text-white tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-blue-700"
                    >
                        <Icon icon="mdi:pencil" className="tw-text-sm tw-flex-shrink-0" /> Edit Vendor
                    </button>
                </div>
            </div>

            {/* Loading State */}
            {vendorDetailsLoading && (
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-text-center tw-py-12 tw-flex tw-flex-col tw-items-center tw-justify-center">
                    <Icon icon="mdi:loading" className="tw-text-4xl tw-text-blue-500 tw-animate-spin" />
                    <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">Loading vendor details...</p>
                </div>
            )}

            {/* Vendor Details */}
            {!vendorDetailsLoading && vendor && (
                <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-4">
                    {/* Main Info Card */}
                    <div className="lg:tw-col-span-2 tw-space-y-4">
                        {/* Basic Info */}
                        <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4">
                            <div className="tw-flex tw-items-start tw-gap-4">
                                <div className="tw-w-16 tw-h-16 tw-rounded-xl tw-bg-blue-500 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                    <Icon icon={getCategoryIcon(vendor.category)} className="tw-text-white tw-text-3xl" />
                                </div>
                                <div className="tw-flex-1">
                                    <div className="tw-flex tw-items-start tw-justify-between">
                                        <div>
                                            <h2 className="tw-text-xl tw-font-semibold tw-text-gray-800">{vendor.name}</h2>
                                            <p className="tw-text-sm tw-text-gray-500">{vendor.contactPerson}</p>
                                        </div>
                                        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                                            <span className={`tw-px-2.5 tw-py-1 tw-rounded tw-text-xs tw-font-medium ${vendor.status === 'Active' ? 'tw-bg-emerald-100 tw-text-emerald-700' : 'tw-bg-gray-100 tw-text-gray-600'}`}>
                                                {vendor.status}
                                            </span>
                                            <span className={`tw-px-2.5 tw-py-1 tw-rounded tw-text-xs tw-font-medium ${getContractBadge(vendor.contractStatus)}`}>
                                                {vendor.contractStatus}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="tw-flex tw-items-center tw-gap-4 tw-mt-3">
                                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-text-gray-600">
                                            <Icon icon={getCategoryIcon(vendor.category)} className="tw-text-gray-400 tw-text-base tw-flex-shrink-0" />
                                            <span>{vendor.category}</span>
                                        </div>
                                        {/* <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-text-gray-600">
                                            <Icon icon="mdi:tools" className="tw-text-gray-400 tw-text-base tw-flex-shrink-0" />
                                            <span>{vendor.servicesCount} services</span>
                                        </div> */}
                                    </div>
                                    {/* <div className="tw-mt-3">
                                        {renderStars(vendor.rating)}
                                    </div> */}
                                </div>
                            </div>
                        </div>

                        {/* Contact Information */}
                        <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4">
                            <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                                <Icon icon="mdi:contacts" className="tw-text-blue-500 tw-text-base tw-flex-shrink-0" />
                                Contact Information
                            </h3>
                            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                                <div className="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-bg-gray-50 tw-rounded-lg">
                                    <div className="tw-w-10 tw-h-10 tw-rounded-lg tw-bg-blue-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                        <Icon icon="mdi:phone" className="tw-text-blue-600 tw-text-lg" />
                                    </div>
                                    <div>
                                        <p className="tw-text-xs tw-text-gray-500">Phone</p>
                                        <p className="tw-text-sm tw-font-medium tw-text-gray-800">{vendor.phone || 'N/A'}</p>
                                    </div>
                                </div>
                                <div className="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-bg-gray-50 tw-rounded-lg">
                                    <div className="tw-w-10 tw-h-10 tw-rounded-lg tw-bg-purple-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                        <Icon icon="mdi:email" className="tw-text-purple-600 tw-text-lg" />
                                    </div>
                                    <div>
                                        <p className="tw-text-xs tw-text-gray-500">Email</p>
                                        <p className="tw-text-sm tw-font-medium tw-text-gray-800">{vendor.email || 'N/A'}</p>
                                    </div>
                                </div>
                                {vendor.website && (
                                    <div className="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-bg-gray-50 tw-rounded-lg">
                                        <div className="tw-w-10 tw-h-10 tw-rounded-lg tw-bg-sky-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                            <Icon icon="mdi:web" className="tw-text-sky-600 tw-text-lg" />
                                        </div>
                                        <div className="tw-min-w-0">
                                            <p className="tw-text-xs tw-text-gray-500">Website</p>
                                            <a href={vendor.website} target="_blank" rel="noreferrer" className="tw-text-sm tw-font-medium tw-text-sky-700 hover:tw-underline tw-break-all">{vendor.website}</a>
                                        </div>
                                    </div>
                                )}
                                {vendor.pincode && (
                                    <div className="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-bg-gray-50 tw-rounded-lg">
                                        <div className="tw-w-10 tw-h-10 tw-rounded-lg tw-bg-amber-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                            <Icon icon="mdi:map-marker-radius" className="tw-text-amber-600 tw-text-lg" />
                                        </div>
                                        <div>
                                            <p className="tw-text-xs tw-text-gray-500">Pincode</p>
                                            <p className="tw-text-sm tw-font-medium tw-text-gray-800">{vendor.pincode}</p>
                                        </div>
                                    </div>
                                )}
                                <div className="tw-col-span-full tw-flex tw-items-center tw-gap-3 tw-p-3 tw-bg-gray-50 tw-rounded-lg">
                                    <div className="tw-w-10 tw-h-10 tw-rounded-lg tw-bg-emerald-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                        <Icon icon="mdi:map-marker" className="tw-text-emerald-600 tw-text-lg" />
                                    </div>
                                    <div>
                                        <p className="tw-text-xs tw-text-gray-500">Address</p>
                                        <p className="tw-text-sm tw-font-medium tw-text-gray-800">{vendor.address || 'N/A'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Contract & Commercial Details */}
                        {(vendor.contractStart || vendor.contractEnd || vendor.contractAmount || vendor.paymentTerms || vendor.vendorGlCode) && (
                            <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4">
                                <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                                    <Icon icon="mdi:file-document-edit" className="tw-text-blue-500 tw-text-base tw-flex-shrink-0" />
                                    Contract &amp; Commercials
                                </h3>
                                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
                                    <div className="tw-p-3 tw-bg-gray-50 tw-rounded-lg">
                                        <p className="tw-text-xs tw-text-gray-500">Contract Period</p>
                                        <p className="tw-text-sm tw-font-medium tw-text-gray-800">
                                            {vendor.contractStart || vendor.contractEnd
                                                ? `${fmtDate(vendor.contractStart) || '—'} → ${fmtDate(vendor.contractEnd) || '—'}`
                                                : 'N/A'}
                                        </p>
                                    </div>
                                    <div className="tw-p-3 tw-bg-gray-50 tw-rounded-lg">
                                        <p className="tw-text-xs tw-text-gray-500">Contract Amount</p>
                                        <p className="tw-text-sm tw-font-medium tw-text-gray-800">{fmtCurrency(vendor.contractAmount) || 'N/A'}</p>
                                    </div>
                                    <div className="tw-p-3 tw-bg-gray-50 tw-rounded-lg">
                                        <p className="tw-text-xs tw-text-gray-500">Payment Terms</p>
                                        <p className="tw-text-sm tw-font-medium tw-text-gray-800 tw-capitalize">{vendor.paymentTerms || 'N/A'}</p>
                                    </div>
                                    <div className="tw-p-3 tw-bg-gray-50 tw-rounded-lg">
                                        <p className="tw-text-xs tw-text-gray-500">Vendor GL Code</p>
                                        <p className="tw-text-sm tw-font-medium tw-text-gray-800 tw-font-mono">{vendor.vendorGlCode || 'N/A'}</p>
                                    </div>
                                    {vendor.experienceYears !== '' && (
                                        <div className="tw-p-3 tw-bg-gray-50 tw-rounded-lg">
                                            <p className="tw-text-xs tw-text-gray-500">Experience</p>
                                            <p className="tw-text-sm tw-font-medium tw-text-gray-800">{vendor.experienceYears} years</p>
                                        </div>
                                    )}
                                </div>
                                {vendor.notes && (
                                    <div className="tw-mt-3 tw-p-3 tw-bg-gray-50 tw-rounded-lg">
                                        <p className="tw-text-xs tw-text-gray-500 tw-mb-1">Notes</p>
                                        <p className="tw-text-sm tw-text-gray-800 tw-whitespace-pre-wrap">{vendor.notes}</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Bank Details */}
                        <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4">
                            <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                                <Icon icon="mdi:bank" className="tw-text-blue-500 tw-text-base tw-flex-shrink-0" />
                                Bank Details
                            </h3>
                            {vendorBankAccountsLoading && (
                                <div className="tw-flex tw-items-center tw-justify-center tw-py-6">
                                    <Icon icon="mdi:loading" className="tw-text-2xl tw-text-blue-500 tw-animate-spin" />
                                </div>
                            )}
                            {!vendorBankAccountsLoading && Array.isArray(vendorBankAccountsData) && vendorBankAccountsData.length > 0 && (
                                <div className="tw-space-y-3">
                                    {vendorBankAccountsData.map((account, index) => (
                                        <div key={account.id || index} className="tw-border tw-border-gray-100 tw-rounded-lg tw-p-3">
                                            <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                                                <p className="tw-text-sm tw-font-medium tw-text-gray-800">{account.bank_name}</p>
                                                <div className="tw-flex tw-items-center tw-gap-2">
                                                    {account.is_primary && (
                                                        <span className="tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium tw-bg-blue-100 tw-text-blue-700">Primary</span>
                                                    )}
                                                    <span className="tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium tw-bg-gray-100 tw-text-gray-600">{account.bank_type || 'Saving'}</span>
                                                </div>
                                            </div>
                                            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-3">
                                                <div className="tw-p-2 tw-bg-gray-50 tw-rounded">
                                                    <p className="tw-text-[10px] tw-text-gray-500">Account Number</p>
                                                    <p className="tw-text-xs tw-font-medium tw-text-gray-800">{account.account_number || 'N/A'}</p>
                                                </div>
                                                <div className="tw-p-2 tw-bg-gray-50 tw-rounded">
                                                    <p className="tw-text-[10px] tw-text-gray-500">IFSC Code</p>
                                                    <p className="tw-text-xs tw-font-medium tw-text-gray-800">{account.ifsc_code || 'N/A'}</p>
                                                </div>
                                                <div className="tw-p-2 tw-bg-gray-50 tw-rounded">
                                                    <p className="tw-text-[10px] tw-text-gray-500">Account Holder</p>
                                                    <p className="tw-text-xs tw-font-medium tw-text-gray-800">{account.account_holder_name || 'N/A'}</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {!vendorBankAccountsLoading && (!vendorBankAccountsData || vendorBankAccountsData.length === 0) && (
                                <div className="tw-text-center tw-py-6">
                                    <Icon icon="mdi:bank-off" className="tw-text-2xl tw-text-gray-300" />
                                    <p className="tw-text-xs tw-text-gray-400 tw-mt-1">No bank accounts added</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="tw-space-y-4">
                        {/* Tax Information */}
                        <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4">
                            <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                                <Icon icon="mdi:file-document" className="tw-text-blue-500 tw-text-base tw-flex-shrink-0" />
                                Tax Information
                            </h3>
                            <div className="tw-space-y-3">
                                <div className="tw-p-3 tw-bg-gray-50 tw-rounded-lg">
                                    <p className="tw-text-xs tw-text-gray-500">GST Number</p>
                                    <p className="tw-text-sm tw-font-medium tw-text-gray-800">{vendor.gstNumber || 'N/A'}</p>
                                </div>
                                <div className="tw-p-3 tw-bg-gray-50 tw-rounded-lg">
                                    <p className="tw-text-xs tw-text-gray-500">PAN Number</p>
                                    <p className="tw-text-sm tw-font-medium tw-text-gray-800">{vendor.panNumber || 'N/A'}</p>
                                </div>
                                <div className="tw-p-3 tw-bg-gray-50 tw-rounded-lg">
                                    <p className="tw-text-xs tw-text-gray-500">TAN Number</p>
                                    <p className="tw-text-sm tw-font-medium tw-text-gray-800">{vendor.tanNumber || 'N/A'}</p>
                                </div>
                                <div className="tw-p-3 tw-bg-gray-50 tw-rounded-lg">
                                    <p className="tw-text-xs tw-text-gray-500">MSME Status</p>
                                    <p className="tw-text-sm tw-font-medium tw-text-gray-800">{vendor.msmeStatus || 'N/A'}</p>
                                </div>
                                <div className="tw-p-3 tw-bg-gray-50 tw-rounded-lg">
                                    <p className="tw-text-xs tw-text-gray-500">RCM Applicable</p>
                                    <p className="tw-text-sm tw-font-medium tw-text-gray-800">{vendor.rcmApplicable === 'Y' ? 'Yes' : 'No'}</p>
                                </div>
                                <div className="tw-p-3 tw-bg-gray-50 tw-rounded-lg">
                                    <p className="tw-text-xs tw-text-gray-500">TDS Applicable</p>
                                    <p className="tw-text-sm tw-font-medium tw-text-gray-800">{vendor.tdsApplicable === 'Y' ? 'Yes' : 'No'}</p>
                                </div>
                            </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4">
                            <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                                <Icon icon="mdi:lightning-bolt" className="tw-text-blue-500 tw-text-base tw-flex-shrink-0" />
                                Quick Actions
                            </h3>
                            <div className="tw-space-y-2">
                                <button
                                    onClick={() => navigate(`/society-portal/vendors/bills?vendor_id=${id}`)}
                                    className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-p-2.5 tw-text-sm tw-text-gray-700 tw-bg-gray-50 hover:tw-bg-gray-100 tw-rounded-lg tw-transition-colors"
                                >
                                    <Icon icon="mdi:receipt" className="tw-text-gray-400 tw-text-base tw-flex-shrink-0" />
                                    View Bills
                                </button>
                                <button
                                    onClick={() => navigate(`/society-portal/vendors/payments?vendor_id=${id}`)}
                                    className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-p-2.5 tw-text-sm tw-text-gray-700 tw-bg-gray-50 hover:tw-bg-gray-100 tw-rounded-lg tw-transition-colors"
                                >
                                    <Icon icon="mdi:cash" className="tw-text-gray-400 tw-text-base tw-flex-shrink-0" />
                                    View Payments
                                </button>
                                <button
                                    onClick={() => navigate(`/society-portal/vendors/${id}/tariffs`)}
                                    className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-p-2.5 tw-text-sm tw-text-gray-700 tw-bg-gray-50 hover:tw-bg-gray-100 tw-rounded-lg tw-transition-colors"
                                >
                                    <Icon icon="mdi:tag-multiple-outline" className="tw-text-gray-400 tw-text-base tw-flex-shrink-0" />
                                    View Tariffs
                                </button>
                            </div>
                        </div>

                        {/* Activity */}
                        <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4">
                            <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                                <Icon icon="mdi:history" className="tw-text-blue-500 tw-text-base tw-flex-shrink-0" />
                                Activity
                            </h3>
                            <div className="tw-space-y-3">
                                <div className="tw-flex tw-items-center tw-gap-3 tw-text-sm">
                                    <div className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-emerald-500"></div>
                                    <span className="tw-text-gray-600">Vendor created</span>
                                </div>
                                <div className="tw-flex tw-items-center tw-gap-3 tw-text-sm">
                                    <div className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-blue-500"></div>
                                    <span className="tw-text-gray-600">{vendor.servicesCount} services completed</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* No Data State */}
            {!vendorDetailsLoading && !vendor && (
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-text-center tw-py-12 tw-flex tw-flex-col tw-items-center tw-justify-center">
                    <Icon icon="mdi:store-off" className="tw-text-4xl tw-text-gray-300" />
                    <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">Vendor not found</p>
                    <button
                        onClick={() => navigate('/society-portal/vendors')}
                        className="tw-mt-4 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-blue-600 hover:tw-bg-blue-50 tw-rounded-lg"
                    >
                        Back to Vendors
                    </button>
                </div>
            )}
        </>
    );
};

export default ViewVendor;
